import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import PulseLoader from 'react-spinners/PulseLoader';

// MaterialUI
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';

// app: constants
import { COMMENT_RULES } from 'constants/index';
import RichTextField from 'components/RichTextField';

// assets
import CssCommentForm from './CommentForm.scss';

const MAX_LENGTH = COMMENT_RULES.note.maxLength;

const NoteForm = React.forwardRef((props, ref) => {
  const {
    focused,
    isOpen,
    note,
    onBlur,
    onChange,
    onClose,
    onFocus,
    onOpen,
    isModerator,
    onSubmit,
    saveInProgress,
    saveError,
    showAsEditButton,
    comment,
  } = props;

  useStyles(CssCommentForm);
  const [isDirty, setDirty] = useState(false);
  const [noteLength, setNoteLength] = useState(0);

  const handleNoteChange = htmlContent => {
    if (!isOpen) {
      onOpen();
    }
    setDirty(true);
    onChange({ note: htmlContent });
  };

  const handleSubmit = event => {
    event.preventDefault();
    onSubmit({
      note,
      id: comment && comment?.id,
      is_new: !comment || !comment?.id,
    });
  };

  const handleClose = () => {
    onChange({ note: null });
    onClose();
  };

  const handleBlur = event => {
    setDirty(!!noteLength);
    onBlur(event);
  };

  if (!isOpen && showAsEditButton) {
    return (
      <div className="comment-form--as-button comment-form__row comment-form__centered margin-top-10">
        <Button
          className="comment-form__activator"
          color="primary"
          size="large"
          variant="outlined"
          onClick={onOpen}
          fullWidth
        >
          Edit your notes
        </Button>
      </div>
    );
  }

  return (
    <form noValidate className="comment-form" onSubmit={handleSubmit}>
      <div className="comment-form__row margin-top-20">
        <RichTextField
          ref={ref}
          fullWidth
          id="note"
          focused={focused}
          isDirty={isDirty}
          handleReviewChange={handleNoteChange}
          review={note}
          maxLength={MAX_LENGTH}
          setReviewLength={setNoteLength}
          onOpen={onOpen}
          isOpen={isOpen}
          isModerator={isModerator}
          handleBlur={handleBlur}
          placeholder="Your notes"
          onFocus={onFocus}
        />
      </div>
      <Collapse in={isOpen} timeout={300}>
        {saveError ? (
          <div className="comment-form__row comment-form__centered margin-top-10">
            <Alert severity="error">{saveError}</Alert>
          </div>
        ) : null}
        <div className="comment-form__row comment-form__buttons">
          <Button
            className="comment-form__button"
            onClick={handleClose}
            size="large"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            className="comment-form__button"
            color="primary"
            disabled={saveInProgress}
            size="large"
            type="submit"
            variant="contained"
          >
            <PulseLoader loading={saveInProgress} color="#fff" sizeUnit="px" size={10} />
            {saveInProgress ? '' : 'Save Note'}
          </Button>
        </div>
      </Collapse>
    </form>
  );
});

NoteForm.propTypes = {
  focused: PropTypes.bool,
  isOpen: PropTypes.bool,
  isModerator: PropTypes.bool,
  note: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onFocus: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  comment: PropTypes.any,
  onOpen: PropTypes.func,
  onSubmit: PropTypes.func,
  saveInProgress: PropTypes.bool,
  saveError: PropTypes.string,
  showAsEditButton: PropTypes.bool,
};

NoteForm.defaultProps = {
  focused: false,
  isOpen: false,
  isModerator: false,
  note: '',
  comment: null,
  onBlur: () => {},
  onChange: () => {},
  onClose: () => {},
  onFocus: () => {},
  onOpen: () => {},
  onSubmit: () => {},
  saveInProgress: false,
  saveError: null,
  showAsEditButton: false,
};

export default React.memo(NoteForm);
